import { useMutationState } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";

/**
 * Returns the request ID for the create/draft request flow.
 *
 * @remarks
 * **Context**: The "create request" page actually works by editing a draft request:
 * - New request: An empty draft is created immediately, then edited on the create page
 * - Edit draft: The create page is reused to edit an existing draft with prefilled data
 * - Duplicate: Fields are populated from the original request, saved as a new draft
 *
 * This differs from the separate "edit" page used for already-submitted requests.
 *
 * **Implementation**: The request ID can come from two sources with different precedence:
 * 1. From URL params (highest priority) - an existing draft or a duplicate
 * 2. From mutation state - when the draft was just created on this page
 *
 * @returns The request ID being edited, or `undefined` while it is unknown
 */
export function useDraftRequestId(): number | undefined {
  const {
    draft_id: draftId,
    duplicate_from: duplicateFrom,
    request_id: requestId,
  } = useSearch({
    from: "/_auth/_app-shell/requests/create",
  });

  const requestIdFromUrl = requestId ?? draftId;

  const data = useMutationState({
    filters: { mutationKey: ["draftId"], status: "success" },
    select: (mutation) => mutation.state.data,
  });
  const requestIdFromDraft = data.at(-1) as { requestId: number } | undefined;

  if (requestIdFromUrl) return requestIdFromUrl;

  if (duplicateFrom) return undefined;

  return requestIdFromDraft?.requestId;
}
